<script lang="ts">
    import ZentitySelect from '../_common/z/zentity-select.svelte'
    import { faMinus, faPlus, faPrint, faShoppingCart } from '@fortawesome/free-solid-svg-icons'
    import { GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../_common/z/zgrid'
    import Popover from '../_common/popover.svelte'
    import { User } from '../../modules/usersApi/users/user'
    import ComissionsForm from './comissions-form.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Fa from 'svelte-fa'
    import DateSelect from '../_common/date-select.svelte'
    import type { Appointment } from '../../modules/schedulerApi/appointments/appointment'
    import Zgrid from '../_common/z/zgrid.svelte'
    import type { Invoice } from '../../modules/posApi/invoices/invoice'
    import { Comission, ComissionTypeEnum } from '../../modules/eventsApi/comissions/comission'
    import ComissionsDetails from './comissions-details.svelte'
    import ComissionsDetailsAppointment from './comissions-details-appointment.svelte'
    import type { IComission, IComissionConsumption, IComissionGroup } from './comissions.interface'
    import { onMount } from 'svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import { calcComission, calcComissionPayable } from '../../modules/use-cases/calc-comission'

    // três origens, três grids: cada uma tem uma forma própria (atendimento agrupa por agendamento+atendente
    // e desconta insumo; venda é por item; lançamento manual é uma linha só). o total consolidado das três
    // fica no resumo do topo, que é o número que a tela existe pra responder: quanto pagar.
    let groups: IComissionGroup[] = []
    let posEntries: IComission[] = []
    let manualEntries: IComission[] = []

    let loading = false
    async function loadEntries() {
        if (!window.store.token.permissions.comissions.entries.self) {
            if (user?.userId === window.store.token.userId) {
                window.error('Você não possui permissão para acessar as comissões deste usuário.')
                return
            }
        }
        if (!window.store.token.permissions.comissions.entries.self || !window.store.token.permissions.comissions.entries.others) {
            if (!user?.userId) return // se não tem permissão para ver as próprias, não pode ver geral.
        }
        if (loading) return
        loading = true
        groups = []
        posEntries = []
        manualEntries = []

        const filter = {
            id: user?.userId || undefined,
            from: dates?.from ? dates?.from : new Date(),
            to: dates?.to ? dates?.to : new Date(),
        }
        let index = 0

        // ---- atendimentos (agenda) ----
        // sem filtro de usuário o backend devolve todos os itens e todo o consumo do agendamento; o
        // agrupamento por atendente é feito aqui (ver findComissions em appointments.repository.ts).
        const appointments: Appointment[] = await window.api.schedulers.appointments.getComissions(filter)
        const groupMap = new Map<string, IComissionGroup>()
        for (const appointment of appointments) {
            for (const item of appointment.items) {
                const key = appointment.appointmentId + '|' + item.userId
                let group = groupMap.get(key)
                if (!group) {
                    group = {
                        id: index++,
                        appointmentId: appointment.appointmentId,
                        appointmentInvoiceId: appointment.invoiceId,
                        date: new Date(appointment.start),
                        userId: item.userId,
                        personId: appointment.personId,
                        items: [],
                        consumption: [],
                        total: 0,
                        comissionGross: 0,
                        consumptionCost: 0,
                        comission: 0,
                    }
                    groupMap.set(key, group)
                }
                // valor CHEIO no item, de propósito: a linha do item exibe quanto a comissão valeria e é
                // marcada como cancelada visualmente. o que não pode contar cancelado é o AGREGADO, logo abaixo.
                const comValue = calcComission(item)
                const comission: IComission = {
                    id: index++,
                    comission: comValue,
                    appointmentInvoiceId: appointment.invoiceId,
                    appointmentItemInvoiceId: item.invoiceId,
                    invoiceIdType: item.invoiceIdType,
                    date: new Date(appointment.start),
                    personId: appointment.personId,
                    source: 'scheduler',
                    sourceId: appointment.appointmentId,
                    sourceItemId: item.appointmentItemId,
                    // desde 26/07/2026 price é espelho de comissionPrice, então o fallback só alcança registros
                    // antigos (quando price guardava o preço de tabela). Ao remover price, deixar só comissionPrice.
                    total: item.comissionPrice || item.price,
                    userId: item.userId,
                    serviceId: item.serviceId,
                    details: {
                        comissionCosts: item.comissionCosts,
                        comissionFeeDebt: item.comissionFeeDebt,
                        comissionFixed: item.comissionFixed,
                        comissionPriceSource: item.comissionPriceSource,
                        comissionRateCustomized: item.comissionRateCustomized || false,
                        comissionPercentage: item.comissionPercentage,
                        comissionCanceled: item.comissionCanceled,
                    },
                }
                group.items.push(comission)
                // Valor base é a REFERÊNCIA da comissão: item cancelado não gera comissão, então também não
                // entra na base. Sem isso o total do grupo não bate com a coluna "Valor base" do grid de itens
                // (que ignora canceladas) nem com o modo expandido, onde a linha cancelada sai da soma.
                if (!item.comissionCanceled) group.total += comission.total || 0
                // REGRA: todo agregado conta só o que será pago. uma linha de item pode ser riscada para dizer
                // "existia, mas não será paga"; um agregado não tem esse recurso — ou entra na conta, ou não entra.
                group.comissionGross += calcComissionPayable(item)
            }
            // consumo atribuído a cada atendente deste agendamento (nunca rateado — ver IComissionConsumption)
            for (const consumption of appointment.consumption || []) {
                if (!(consumption.comissionCost > 0)) continue
                const key = appointment.appointmentId + '|' + consumption.userId
                let group = groupMap.get(key)
                if (!group) {
                    // consumo de um atendente que não tem item de comissão neste agendamento: ainda assim
                    // precisa aparecer, senão o débito some da tela sem ninguém perceber.
                    group = {
                        id: index++,
                        appointmentId: appointment.appointmentId,
                        appointmentInvoiceId: appointment.invoiceId,
                        date: new Date(appointment.start),
                        userId: consumption.userId,
                        personId: appointment.personId,
                        items: [],
                        consumption: [],
                        total: 0,
                        comissionGross: 0,
                        consumptionCost: 0,
                        comission: 0,
                    }
                    groupMap.set(key, group)
                }
                const line: IComissionConsumption = {
                    appointmentConsumptionId: consumption.appointmentConsumptionId,
                    serviceId: consumption.serviceId,
                    quantity: consumption.quantity,
                    comissionCost: consumption.comissionCost,
                    comissionCanceled: consumption.comissionCanceled || false,
                }
                group.consumption.push(line)
                // insumo estornado continua na lista (com o valor cheio, riscado) mas não é cobrado do
                // atendente — mesma regra dos itens: agregado só conta o que será efetivamente cobrado.
                if (!line.comissionCanceled) group.consumptionCost += line.comissionCost
            }
        }
        for (const group of groupMap.values()) {
            // negativo é intencional: insumo mais caro que a comissão normalmente é erro de cadastro e
            // precisa ficar explícito no valor, não truncado em zero.
            group.comission = group.comissionGross - group.consumptionCost
            // some da lista só quando não há absolutamente nada a mostrar (mesmo critério do grid antigo,
            // que pulava itens com comissão zerada). os dois testes olham o que EXISTE, nunca o pagável:
            // comissão cancelada ou insumo estornado zeram o pagável e sumiriam da tela — junto com a chance
            // de reativá-los pelo popover de detalhes.
            if (group.items.some((el) => el.comission !== 0) || group.consumption.length > 0) groups.push(group)
        }

        // ---- vendas (pdv) ----
        const invoices: Invoice[] = await window.api.pos.invoices.getComissions(filter)
        for (const invoice of invoices) {
            for (const item of invoice.services) {
                // valor CHEIO na linha, de propósito: a venda cancelada continua listada (riscada, e ainda dá
                // para reativá-la pelo popover). quem desconta o cancelamento é o agregado, em totalPos.
                const comValue = calcComission(item)
                if (comValue === 0) continue
                posEntries.push({
                    id: index++,
                    comission: comValue,
                    invoiceIdType: null,
                    date: new Date(invoice.createdAt),
                    personId: invoice.personId,
                    source: 'pos',
                    sourceId: invoice.invoiceId,
                    sourceItemId: item.servicesOnInvoicesId,
                    total: item.value * item.quantity,
                    userId: item.userId,
                    serviceId: item.serviceId,
                    details: {
                        comissionCosts: item.comissionCosts,
                        comissionFeeDebt: item.comissionFeeDebt,
                        comissionFixed: item.comissionFixed,
                        comissionPercentage: item.comissionPercentage,
                        comissionPriceSource: null,
                        comissionRateCustomized: item.comissionRateCustomized || false,
                        comissionCanceled: item.comissionCanceled,
                    },
                })
            }
        }

        // ---- lançamentos manuais ----
        const comissions: Comission[] = await window.api.events.comissions.getAll({
            userId: user?.userId,
            from: dates?.from ? dates?.from : new Date(),
            to: dates?.to ? dates?.to : new Date(),
        })
        for (const com of comissions) {
            manualEntries.push({
                id: index++,
                comission: com.type === ComissionTypeEnum.credit ? com.value : -com.value,
                date: new Date(com.paidAt),
                personId: null,
                source: 'manual',
                sourceId: com.comissionId,
                sourceItemId: null,
                invoiceIdType: null,
                total: null,
                item: com.description,
                userId: com.userId,
                serviceId: null,
                details: null,
            })
        }

        // resolve nomes (os itens/consumo são os mesmos objetos referenciados pelos grupos)
        const allItems = groups.reduce((acc, el) => acc.concat(el.items), [] as IComission[])
        const allConsumption = groups.reduce((acc, el) => acc.concat(el.consumption), [] as IComissionConsumption[])
        await window.api.utils.utils.assignServicesFromObject(allItems)
        await window.api.utils.utils.assignServicesFromObject(allConsumption)
        await window.api.utils.utils.assignServicesFromObject(posEntries)
        await window.api.utils.utils.assignPersonsFromObject(groups)
        await window.api.utils.utils.assignPersonsFromObject(posEntries)
        await window.api.utils.utils.assignUsersFromObject(groups)
        await window.api.utils.utils.assignUsersFromObject(posEntries)
        await window.api.utils.utils.assignUsersFromObject(manualEntries)

        groups.sort((a, b) => +a.date - +b.date)
        posEntries.sort((a, b) => +a.date - +b.date)
        manualEntries.sort((a, b) => +a.date - +b.date)

        // reatribuição para disparar a reatividade do Svelte: .push()/.sort() só mutam o array, e os totais
        // do topo (totalAppointments, totalPos, totalManual, totalGeral) são $: derivados de groups/posEntries/
        // manualEntries. sem isso eles ficam presos no valor calculado logo após "groups = []" (arrays ainda
        // vazios) e nunca refletem os dados populados — mesmo com os grids abaixo corretos, pois esses SÃO
        // reatribuídos em loadGrids() (appointmentsGrid = grid, etc).
        groups = groups
        posEntries = posEntries
        manualEntries = manualEntries

        loadGrids()
        loading = false
    }

    // ---- resumo ----
    // estes cards precisam bater com o rodapé de cada grid. o getTotal do Zgrid soma as linhas ignorando as
    // marcadas como removidas (= canceladas), então aqui a comissão cancelada também não pode entrar — senão
    // o mesmo número aparece diferente no topo e no rodapé da mesma tela.
    // groups já vem com comissionGross calculado por calcComissionPayable; posEntries guarda o valor cheio
    // (para exibir na linha), então o cancelamento é descontado aqui.
    $: totalAppointments = groups.reduce((acc, el) => acc + el.comission, 0)
    $: totalConsumption = groups.reduce((acc, el) => acc + el.consumptionCost, 0)
    $: totalPos = posEntries.reduce((acc, el) => acc + (el.details?.comissionCanceled ? 0 : el.comission), 0)
    $: totalManual = manualEntries.reduce((acc, el) => acc + el.comission, 0)
    $: totalGeral = totalAppointments + totalPos + totalManual

    // modo expandido do grid de atendimentos: uma linha por item/insumo em vez de uma por atendimento.
    // só troca a montagem das linhas — não recarrega nada do servidor (ver appendExpandedLines).
    let expanded = false

    let appointmentsGrid: GridClass = new GridClass()
    let posGrid: GridClass = new GridClass()
    let manualGrid: GridClass = new GridClass()

    function baseGrid(title: string): GridClass {
        const grid = new GridClass()
        grid.title = title
        grid.minWidth = '800px'
        grid.fontSize = '13px'
        return grid
    }

    function loadGrids() {
        loadAppointmentsGrid()
        loadPosGrid()
        loadManualGrid()
    }

    function loadAppointmentsGrid() {
        // coluna de insumo só aparece pra quem usa o recurso — evita poluir a tela de quem não lança consumo.
        // testa a existência do consumo, não o custo: com todo o insumo estornado o custo zera, mas as colunas
        // precisam continuar visíveis para o usuário enxergar (e poder reverter) o estorno.
        const anyConsumption = groups.some((el) => el.consumption.length > 0)
        const grid = baseGrid('Atendimentos')
        grid.subtitle = expanded
            ? 'Comissões da agenda, item a item, com os insumos consumidos debitados em linha própria.'
            : 'Comissões da agenda, agrupadas por atendimento e atendente.'
        grid.cols.push(new GridCol({ content: 'Data', type: GridColTypeEnum.datetime }))
        if (!user?.userId) grid.cols.push(new GridCol({ content: 'Atendente' }))
        grid.cols.push(new GridCol({ content: 'Cliente' }))
        grid.cols.push(new GridCol({ content: expanded ? 'Item' : 'Itens' }))
        grid.cols.push(new GridCol({ content: 'Status' }))
        grid.cols.push(new GridCol({ content: 'Valor base', type: GridColTypeEnum.float_2, showTotal: true }))
        if (anyConsumption) {
            grid.cols.push(new GridCol({ content: 'Comissão', type: GridColTypeEnum.float_2, showTotal: true }))
            grid.cols.push(new GridCol({ content: '(-) Insumos', type: GridColTypeEnum.float_2, showTotal: true }))
        }
        grid.cols.push(new GridCol({ content: 'Líquido', type: GridColTypeEnum.float_2, showTotal: true }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        for (const group of groups) {
            if (expanded) {
                appendExpandedLines(grid, group, anyConsumption)
                continue
            }
            const line = new GridLine()
            // o getTotal do Zgrid ignora a linha inteira quando removed=true. como o valor do grupo já é o
            // pagável, só marque removida quando a linha não contribui com nada — um atendimento com todas as
            // comissões canceladas mas com insumo a debitar ainda tem saldo (negativo) que precisa ser somado.
            const allCanceled = group.items.length > 0 && group.items.every((el) => el.details?.comissionCanceled)
            line.removed = allCanceled && group.comission === 0
            line.slots.push({ content: group.date })
            if (!user?.userId) line.slots.push({ content: group.user?.userName || '-' })
            line.slots.push({ content: group.person?.name || '-' })
            line.slots.push({ content: itemsLabel(group) })
            line.slots.push({ content: statusLabel(group) })
            line.slots.push({ content: group.total })
            if (anyConsumption) {
                line.slots.push({ content: group.comissionGross })
                line.slots.push({ content: -group.consumptionCost })
            }
            line.slots.push({ content: group.comission })
            line.slots.push({
                content: [new GridButton({ label: 'Detalhes', callback: () => (detailGroup = group) })],
            })
            grid.lines.push(line)
        }
        appointmentsGrid = grid
    }

    // ---------------------------------------------------------------------------------------------------
    // MODO EXPANDIDO (recurso opcional, ligado pelo switch "Expandir" na barra)
    // Substitui a linha única do atendimento por uma linha para cada item e cada insumo. Existe porque parte
    // dos clientes preferia a listagem plana que havia antes do agrupamento por atendimento.
    //
    // Os valores são uma PARTIÇÃO exata dos mesmos números da linha agrupada — nada aqui recalcula comissão.
    // Como o getTotal do Zgrid soma linha a linha, o rodapé e os cards do topo dão o mesmo em ambos os modos.
    // Por isso as linhas usam o valor CHEIO e delegam a exclusão do cancelado/estornado ao `removed`,
    // exatamente como as demais telas fazem.
    //
    // PARA REMOVER O RECURSO: apagar esta função, o bloco `if (expanded)` em loadAppointmentsGrid, a variável
    // `expanded` e o switch na barra de ações. Nada mais depende dele.
    // ---------------------------------------------------------------------------------------------------
    function appendExpandedLines(grid: GridClass, group: IComissionGroup, anyConsumption: boolean) {
        for (const item of group.items) {
            const line = new GridLine()
            line.removed = item.details?.comissionCanceled || false
            line.slots.push({ content: group.date })
            if (!user?.userId) line.slots.push({ content: group.user?.userName || '-' })
            line.slots.push({ content: group.person?.name || '-' })
            line.slots.push({ content: item.service?.name || '-' })
            line.slots.push({ content: line.removed ? 'cancelada' : 'paga' })
            line.slots.push({ content: item.total })
            if (anyConsumption) {
                line.slots.push({ content: item.comission })
                line.slots.push({ content: 0 })
            }
            line.slots.push({ content: item.comission })
            // abre o detalhe do ITEM (e não do atendimento): é o acesso direto que existia antes do
            // agrupamento — inclusive para corrigir a venda de referência da comissão.
            line.slots.push({
                content: [new GridButton({ label: 'Detalhes', callback: () => (detail = item) })],
            })
            grid.lines.push(line)
        }
        for (const consumption of group.consumption) {
            const line = new GridLine()
            line.removed = consumption.comissionCanceled || false
            line.slots.push({ content: group.date })
            if (!user?.userId) line.slots.push({ content: group.user?.userName || '-' })
            line.slots.push({ content: group.person?.name || '-' })
            line.slots.push({ content: consumptionLabel(consumption) })
            line.slots.push({ content: line.removed ? 'estornado' : 'cobrado' })
            line.slots.push({ content: 0 }) // insumo não tem valor base — a coluna é do serviço prestado
            if (anyConsumption) {
                line.slots.push({ content: 0 })
                line.slots.push({ content: -consumption.comissionCost })
            }
            line.slots.push({ content: -consumption.comissionCost })
            // o estorno do insumo mora no detalhe do atendimento (onde está o grid de insumos)
            line.slots.push({
                content: [new GridButton({ label: 'Detalhes', callback: () => (detailGroup = group) })],
            })
            grid.lines.push(line)
        }
    }

    function consumptionLabel(consumption: IComissionConsumption) {
        const name = consumption.service?.name || 'Insumo'
        return consumption.quantity ? name + ' (' + consumption.quantity + ')' : name
    }
    // --------------------------------------------------------------- fim do modo expandido

    function loadPosGrid() {
        const grid = baseGrid('Vendas')
        grid.subtitle = 'Comissões de vendas lançadas no PDV.'
        grid.cols.push(new GridCol({ content: 'Data', type: GridColTypeEnum.datetime }))
        if (!user?.userId) grid.cols.push(new GridCol({ content: 'Vendedor' }))
        grid.cols.push(new GridCol({ content: 'Item' }))
        grid.cols.push(new GridCol({ content: 'Cliente' }))
        grid.cols.push(new GridCol({ content: 'Status' }))
        grid.cols.push(new GridCol({ content: 'Valor base', type: GridColTypeEnum.float_2, showTotal: true }))
        grid.cols.push(new GridCol({ content: 'Líquido', type: GridColTypeEnum.float_2, showTotal: true }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        for (const item of posEntries) {
            const line = new GridLine()
            line.removed = item.details?.comissionCanceled || false
            line.slots.push({ content: item.date })
            if (!user?.userId) line.slots.push({ content: item.user?.userName || '-' })
            line.slots.push({ content: item.service?.name || '-' })
            line.slots.push({ content: item.person?.name || '-' })
            line.slots.push({ content: line.removed ? 'cancelada' : 'paga' })
            line.slots.push({ content: item.total })
            line.slots.push({ content: item.comission })
            line.slots.push({
                content: [new GridButton({ label: 'Detalhes', callback: () => (detail = item) })],
            })
            grid.lines.push(line)
        }
        posGrid = grid
    }

    function loadManualGrid() {
        const grid = baseGrid('Lançamentos manuais')
        grid.subtitle = 'Créditos e débitos lançados diretamente nesta tela.'
        grid.cols.push(new GridCol({ content: 'Data', type: GridColTypeEnum.datetime }))
        if (!user?.userId) grid.cols.push(new GridCol({ content: 'Usuário' }))
        grid.cols.push(new GridCol({ content: 'Descrição' }))
        grid.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.float_2, showTotal: true }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        for (const item of manualEntries) {
            const line = new GridLine()
            line.slots.push({ content: item.date })
            if (!user?.userId) line.slots.push({ content: item.user?.userName || '-' })
            line.slots.push({ content: item.item || '-' })
            line.slots.push({ content: item.comission })
            line.slots.push({
                content: [new GridButton({ label: 'Editar', callback: () => editEntry(item.sourceId) })],
            })
            grid.lines.push(line)
        }
        manualGrid = grid
    }

    function itemsLabel(group: IComissionGroup) {
        if (group.items.length === 0) return 'Somente consumo de insumo'
        if (group.items.length === 1) return group.items[0].service?.name || '-'
        return group.items.length + ' itens'
    }

    function statusLabel(group: IComissionGroup) {
        if (group.items.length === 0) return '-'
        const canceled = group.items.filter((el) => el.details?.comissionCanceled).length
        if (canceled === 0) return 'paga'
        if (canceled === group.items.length) return 'cancelada'
        return 'parcial'
    }

    let detail: IComission = null
    let detailGroup: IComissionGroup = null

    let showEntry = false
    let comissionId: string = null
    let creditDebit: ComissionTypeEnum
    function editEntry(id) {
        comissionId = id
        showEntry = true
    }

    function newEntry(_creditDebit: ComissionTypeEnum) {
        comissionId = null
        creditDebit = _creditDebit
        showEntry = true
    }

    function entryChanged() {
        loadEntries()
        showEntry = false
    }
    function entryCanceled() {
        loadEntries() // como podem ocorrer lançamentos em sequencia e depois o usuário sair clicando em "fechar", sempre atualizar
        showEntry = false
    }

    let user: User = null
    let dateSelect

    let dates: { from: Date; to: Date } = window.api.utils.dates.currentMonth()
    function dateChanged(event) {
        dates = event.detail
        loadEntries()
    }

    function comissionsDetailsOnChange() {
        detail = null
        detailGroup = null
        loadEntries()
    }

    onMount(async () => {
        if (!window.store.token.permissions.comissions.entries.others) {
            user = new User()
            user.userId = window.store.token.userId
        }
        await loadEntries()
    })
</script>

{#if showEntry}
    <Popover title="Lançamento" width="980px" height="260px" on:close={entryCanceled}>
        <ComissionsForm {creditDebit} {comissionId} on:change={entryChanged} />
    </Popover>
{/if}

{#if detail}
    <Popover title="Detalhes" width="980px" height="200px" on:close={() => (detail = null)}>
        <ComissionsDetails {detail} on:change={comissionsDetailsOnChange} />
    </Popover>
{/if}

{#if detailGroup}
    <Popover title="Detalhes do atendimento" width="1300px" height="80vh" on:close={() => (detailGroup = null)}>
        <ComissionsDetailsAppointment group={detailGroup} on:change={comissionsDetailsOnChange} />
    </Popover>
{/if}

<LoadSpinner show={loading} />
<div>
    <Zact>
        {#if window.store.token.permissions.comissions.entries.others}
            <ZentitySelect fonte="usuario" label="Usuário" bind:value={user} opcaoVazia="Todos" class="limit-w_400 me-1" on:change={loadEntries} />
        {/if}
        <DateSelect bind:this={dateSelect} startFrom={dates.from} startTo={dates.to} on:change={dateChanged} class="me-1" />
        <!-- modo expandido: só remonta o grid, não recarrega dados (ver appendExpandedLines) -->
        <div class="form-check form-switch expand-switch me-1" title="Lista cada item e insumo em sua própria linha, em vez de agrupar por atendimento.">
            <input class="form-check-input" bind:checked={expanded} on:change={loadAppointmentsGrid} type="checkbox" role="switch" id="comissionsExpand" />
            <label class="form-check-label" for="comissionsExpand">Expandir</label>
        </div>
        <div class="spacer" />
        <button class="btn btn-secondary me-1 h-mobile" title="Imprimir" on:click={() => window.print()}><Fa icon={faPrint} /></button>
        <button class="btn btn-primary me-1 h-mobile" on:click={() => newEntry(ComissionTypeEnum.credit)}><Fa icon={faPlus} /> Creditar</button>
        <button class="btn btn-primary h-mobile" on:click={() => newEntry(ComissionTypeEnum.debit)}><Fa icon={faMinus} /> Debitar</button>
    </Zact>

    <div class="summary h-space">
        <div class="-item">
            <div class="-label">Atendimentos</div>
            <div class="-value">{window.api.utils.utils.toReal(totalAppointments)}</div>
            {#if totalConsumption > 0}
                <div class="-note">já com {window.api.utils.utils.toReal(totalConsumption)} de insumos descontados</div>
            {/if}
        </div>
        <div class="-item">
            <div class="-label">Vendas</div>
            <div class="-value">{window.api.utils.utils.toReal(totalPos)}</div>
        </div>
        <div class="-item">
            <div class="-label">Lançamentos manuais</div>
            <div class="-value">{window.api.utils.utils.toReal(totalManual)}</div>
        </div>
        <div class="-item -total">
            <div class="-label">Total a pagar</div>
            <div class="-value" class:negative={totalGeral < 0}>{window.api.utils.utils.toReal(totalGeral)}</div>
            <div class="-note">{user?.userId ? user?.userName || 'atendente selecionado' : 'todos os usuários'}</div>
        </div>
    </div>

    <Zgrid class="h-space" grid={appointmentsGrid} />
    <Zgrid class="h-space" grid={posGrid} />
    <Zgrid class="h-space" grid={manualGrid} />

    <div class="px-1 mt-2">
        <div class="text-muted">1. Apenas compras com status "fechado" ou "baixado" entram no relatório de comissões do vendedor.</div>
        <div class="text-muted">2. Apenas agendamentos com status "atendido" entram no relatório de comissões do atendente.</div>
        <div class="text-muted">3. Comissões do atendente são pagas pelo valor de tabela. Para mudar este comportamento clique na aba "configurações".</div>
        <div class="text-muted">4. Para comissões do atendente com base no preço pago pelo cliente (e não de tabela):</div>
        <div class="text-muted margin-left">I. elas são baseadas na última compra do cliente lançada no PDV com data anterior ao atendimento na agenda.</div>
        <div class="text-muted margin-left">
            II. caso seja utilizado o ícone <Fa icon={faShoppingCart} class="h-print" /> da agenda para lançar o pagamento no PDV, a comissão será baseada neste
            lançamento.
        </div>
        <div class="text-muted margin-left">
            III. caso não haja um lançamento no PDV para o cliente, então a comissão do atendente será baseada no preço de tabela.
        </div>
        <div class="text-muted">
            5. O custo de insumos consumidos no atendimento é descontado do atendente escolhido no lançamento do consumo — nunca dividido entre vários.
        </div>
    </div>
</div>

<style>
    .margin-left {
        margin-left: 16px;
    }
    /* alinha o switch com os demais controles da barra (o form-check do Bootstrap usa float + padding) */
    .expand-switch {
        display: flex;
        align-items: center;
        gap: 6px;
        padding-left: 0;
        margin-bottom: 0;
        white-space: nowrap;
    }
    .expand-switch :global(.form-check-input) {
        float: none;
        margin: 0;
    }
    .expand-switch :global(.form-check-label) {
        font-size: 13px;
        cursor: pointer;
    }
    .summary {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }
    .summary .-item {
        flex: 1;
        min-width: 160px;
        padding: 10px 14px;
        border-radius: 4px;
        background: #99999918;
    }
    .summary .-item.-total {
        background: #0d6efd18;
    }
    .summary .-label {
        font-size: 12px;
        opacity: 0.75;
    }
    .summary .-value {
        font-size: 20px;
        font-weight: 300;
    }
    .summary .-value.negative {
        color: #dc3545;
    }
    .summary .-note {
        font-size: 11px;
        opacity: 0.6;
    }
</style>
